import { useEffect, useState, ChangeEvent, FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CheckCircle2,
  UploadCloud,
  X,
  Loader2,
  ArrowLeft,
  Phone,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { drywallServices, r3Services } from "@/data/content";
import { submitBidRequest } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/site";

const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024;
const PROJECT_TYPES = ["residential", "commercial"];
const PROJECT_CATEGORIES = ["newConstruction", "remodel", "other"];

const inputCls =
  "w-full border border-white/10 bg-charcoal px-5 py-4 text-sm text-white placeholder:text-neutral-600 transition-colors duration-300 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40";

export default function BidRequestPage() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    address: "",
    startDate: "",
    projectType: "residential",
    category: "newConstruction",
    message: "",
    language: lang,
  });
  const [selected, setSelected] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [hp, setHp] = useState("");
  const [categoryOther, setCategoryOther] = useState("");
  const [serviceOther, setServiceOther] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done">("idle");

  useEffect(() => {
    setForm((f) => ({ ...f, language: lang }));
  }, [lang]);

  const set = (key: string) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleService = (slug: string) =>
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );

  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || []);
    const valid: File[] = [];
    for (const f of incoming) {
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} — 10MB max`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid].slice(0, MAX_FILES));
    e.target.value = "";
  };

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("company", form.company);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("project_type", form.projectType);
      fd.append("project_category", form.category);
      fd.append("address", form.address);
      fd.append("start_date", form.startDate);
      fd.append("message", form.message);
      fd.append("language", form.language);
      fd.append("website", hp);
      if (form.category === "other") fd.append("category_other_text", categoryOther);
      if (selected.includes("other")) fd.append("service_other_text", serviceOther);
      selected.forEach((s) => fd.append("services", s));
      files.forEach((f) => fd.append("files", f));
      await submitBidRequest(fd);
      setStatus("done");
    } catch (err) {
      toast.error(t.bid.error);
      setStatus("idle");
    }
  };

  const chipCls = (active: boolean) =>
    `cursor-pointer rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-300 ${
      active
        ? "border-gold bg-gold text-ink"
        : "border-white/15 text-neutral-300 hover:border-gold/50 hover:text-gold"
    }`;

  return (
    <main data-testid="bid-page" className="bg-ink pb-28 pt-32 md:pt-40">
      <div className="mx-auto max-w-3xl px-5 md:px-8">
        <Reveal>
          <Link
            data-testid="bid-back-link"
            to="/"
            className="mb-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 transition-colors duration-300 hover:text-gold"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            R3stan Drywall, Inc.
          </Link>
          <SectionLabel text={t.bid.label} testId="bid-label" />
          <h1
            data-testid="bid-heading"
            className="mt-5 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-6xl"
          >
            {t.bid.heading}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400">
            {t.bid.sub}
          </p>
        </Reveal>

        {status === "done" ? (
          <motion.div
            data-testid="bid-success-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-14 border border-gold/30 bg-charcoal p-10 text-center md:p-14"
          >
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.5} />
            <h2 className="mt-6 font-display text-3xl font-black uppercase tracking-tight text-white md:text-4xl">
              {t.bid.successTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400 md:text-base">
              {t.bid.successText}
            </p>
            <p data-testid="bid-success-contact" className="mx-auto mt-4 max-w-md text-xs font-bold uppercase tracking-[0.15em] text-gold">
              {selected.some((s) => r3Services.some((r) => r.slug === s)) &&
              selected.some((s) => drywallServices.some((d) => d.slug === s))
                ? `${t.bid.contactDrywall} · ${t.bid.contactR3}`
                : selected.some((s) => r3Services.some((r) => r.slug === s))
                  ? t.bid.contactR3
                  : t.bid.contactDrywall}
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <a
                data-testid="bid-success-call-btn"
                href={PHONE_HREF}
                className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
              >
                <Phone className="h-3.5 w-3.5" />
                {PHONE_DISPLAY}
              </a>
              <button
                data-testid="bid-again-btn"
                onClick={() => {
                  setStatus("idle");
                  setForm({
                    name: "",
                    company: "",
                    email: "",
                    phone: "",
                    address: "",
                    startDate: "",
                    projectType: "residential",
                    category: "newConstruction",
                    message: "",
                    language: lang,
                  });
                  setSelected([]);
                  setFiles([]);
                  setCategoryOther("");
                  setServiceOther("");
                }}
                className="rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                {t.bid.again}
              </button>
            </div>
          </motion.div>
        ) : (
          <Reveal delay={0.1}>
            <form
              data-testid="bid-form"
              onSubmit={onSubmit}
              className="mt-12 space-y-9 border border-white/10 bg-[#0d0d0d] p-6 md:p-10"
            >
              <input
                type="text"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.bid.fullName}
                  </label>
                  <input
                    data-testid="bid-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.bid.company}
                  </label>
                  <input
                    data-testid="bid-company-input"
                    value={form.company}
                    onChange={set("company")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.bid.email}
                  </label>
                  <input
                    data-testid="bid-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.bid.phone}
                  </label>
                  <input
                    data-testid="bid-phone-input"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    className={inputCls}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.bid.address}
                  </label>
                  <input
                    data-testid="bid-address-input"
                    value={form.address}
                    onChange={set("address")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.bid.startDate}
                  </label>
                  <input
                    data-testid="bid-startdate-input"
                    type="date"
                    value={form.startDate}
                    onChange={set("startDate")}
                    className={inputCls}
                  />
                </div>
              </div>

              <fieldset>
                <legend className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  {t.bid.projectType}
                </legend>
                <div className="flex flex-wrap gap-2.5">
                  {PROJECT_TYPES.map((pt) => (
                    <button
                      key={pt}
                      type="button"
                      data-testid={`bid-projecttype-${pt}`}
                      onClick={() => setForm((f) => ({ ...f, projectType: pt }))}
                      className={chipCls(form.projectType === pt)}
                    >
                      {t.bid.projectTypes[pt]}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  {t.bid.category}
                </legend>
                <div className="flex flex-wrap gap-2.5">
                  {PROJECT_CATEGORIES.map((pc) => (
                    <button
                      key={pc}
                      type="button"
                      data-testid={`bid-category-${pc}`}
                      onClick={() => setForm((f) => ({ ...f, category: pc }))}
                      className={chipCls(form.category === pc)}
                    >
                      {t.bid.categories[pc]}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  {t.bid.servicesLegend}
                </legend>
                <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                  {t.bid.drywallGroup}
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {drywallServices.map((s) => (
                    <button
                      key={s.slug}
                      type="button"
                      data-testid={`bid-service-${s.slug}`}
                      onClick={() => toggleService(s.slug)}
                      className={chipCls(selected.includes(s.slug))}
                    >
                      {t.services.items[s.slug].title}
                    </button>
                  ))}
                </div>
                <p className="mb-2.5 mt-6 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                  {t.bid.r3Group}
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {r3Services.map((s) => (
                    <button
                      key={s.slug}
                      type="button"
                      data-testid={`bid-service-${s.slug}`}
                      onClick={() => toggleService(s.slug)}
                      className={chipCls(selected.includes(s.slug))}
                    >
                      {t.r3.items[s.slug].title}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="bid-service-other"
                    onClick={() => toggleService("other")}
                    className={chipCls(selected.includes("other"))}
                  >
                    {t.bid.categories.other}
                  </button>
                </div>
                {selected.includes("other") && (
                  <input
                    data-testid="bid-service-other-input"
                    required
                    value={serviceOther}
                    onChange={(e) => setServiceOther(e.target.value)}
                    placeholder={t.bid.otherDescribe}
                    className={`${inputCls} mt-4`}
                  />
                )}
              </fieldset>

              <div>
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  {t.bid.message}
                </label>
                <textarea
                  data-testid="bid-message-input"
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t.bid.messagePh}
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div>
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  {t.bid.upload}
                </label>
                <label
                  data-testid="bid-upload-zone"
                  className="flex cursor-pointer flex-col items-center gap-3 border border-dashed border-white/20 bg-charcoal px-6 py-10 text-center transition-colors duration-300 hover:border-gold/50"
                >
                  <UploadCloud className="h-8 w-8 text-gold" strokeWidth={1.5} />
                  <span className="text-sm font-bold text-white">{t.bid.uploadCta}</span>
                  <span className="text-xs text-neutral-500">{t.bid.uploadHint}</span>
                  <input
                    data-testid="bid-file-input"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={onFiles}
                    className="sr-only"
                  />
                </label>
                {files.length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="bid-file-list">
                    {files.map((f, i) => (
                      <li
                        key={`${f.name}-${i}`}
                        className="flex items-center justify-between border border-white/10 bg-charcoal px-4 py-3 text-sm text-neutral-300"
                      >
                        <span className="truncate pr-4">{f.name}</span>
                        <button
                          type="button"
                          data-testid={`bid-file-remove-${i}`}
                          onClick={() => removeFile(i)}
                          className="text-neutral-500 transition-colors duration-300 hover:text-gold"
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <fieldset>
                <legend className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  {t.bid.language}
                </legend>
                <div className="flex gap-2.5">
                  <button
                    type="button"
                    data-testid="bid-lang-en"
                    onClick={() => setForm((f) => ({ ...f, language: "en" }))}
                    className={chipCls(form.language === "en")}
                  >
                    {t.bid.langEn}
                  </button>
                  <button
                    type="button"
                    data-testid="bid-lang-es"
                    onClick={() => setForm((f) => ({ ...f, language: "es" }))}
                    className={chipCls(form.language === "es")}
                  >
                    {t.bid.langEs}
                  </button>
                </div>
              </fieldset>

              <button
                data-testid="bid-submit-btn"
                type="submit"
                disabled={status === "sending"}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark disabled:opacity-60 sm:w-auto"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.bid.submitting}
                  </>
                ) : (
                  t.bid.submit
                )}
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </main>
  );
}
